import { supabase } from '../../lib/supabase';

import { evaluateAchievements, newlyUnlockedAchievements, type UserXP, type UserStreak, type UserAchievement } from './gamificationService';

interface BatchGamificationData {
  beforeXP: UserXP | null;
  afterXP: UserXP | null;
  beforeAchievements: UserAchievement[];
  afterAchievements: UserAchievement[];
  streakInfo: UserStreak | null;
  earnedXP: number;
  levelUp: boolean;
  newLevel: number;
  newAchievements: UserAchievement[];
}

interface BatchGamificationParams {
  userId: string;
  score: number;
  totalQuestions: number;
  correctCount: number;
  categoryId: number;
  difficulty: string;
  isFirstQuiz?: boolean;
  gameType: 'single' | 'multiplayer';
  gameContext?: {
    rank?: number;
    totalPlayers?: number;
    roomId?: string;
    familyId?: string | null;
  };
  // Enhanced scoring fields
  partialCreditScore?: number;
  hasPartialCredit?: boolean;
  bonusPoints?: number;
  finalScore?: number;
}

/**
 * Batch process all gamification operations for better performance
 * This reduces the number of database round trips from ~8 to ~3
 */
export async function batchProcessGamification(params: BatchGamificationParams): Promise<BatchGamificationData> {
  const { userId, totalQuestions, correctCount, categoryId, difficulty, isFirstQuiz, gameType, gameContext } = params;
  
  console.log('🚀 [batchProcessGamification] Starting batch processing for user:', userId);
  
  // Step 1: Fetch all "before" data in parallel
  console.log('📊 [batchProcessGamification] Step 1: Fetching initial user data...');
  const [beforeXPResult, beforeAchievementsResult, beforeStreakResult] = await Promise.allSettled([
    supabase
      .from('user_xp')
      .select('*')
      .eq('user_id', userId)
      .single(),
    supabase
      .from('user_achievements')
      .select(`
        *,
        achievement:achievements(*)
      `)
      .eq('user_id', userId),
    supabase
      .from('user_streaks')
      .select('*')
      .eq('user_id', userId)
      .single()
  ]);

  const beforeXP = beforeXPResult.status === 'fulfilled' ? beforeXPResult.value.data : null;
  const beforeAchievements = beforeAchievementsResult.status === 'fulfilled' ? beforeAchievementsResult.value.data || [] : [];
  const beforeStreak = beforeStreakResult.status === 'fulfilled' ? beforeStreakResult.value.data : null;
  
  console.log('📊 [batchProcessGamification] Before data:', {
    xp: beforeXP?.total_xp || 0,
    level: beforeXP?.current_level || 1,
    achievements: beforeAchievements.length,
    streak: beforeStreak?.current_streak || 0
  });

  // Step 2: Calculate XP amount and award it (with partial credit support)
  console.log('💎 [batchProcessGamification] Step 2: Calculating and awarding XP...');
  const xpAmount = calculateEnhancedXPAmount(params);
  
  const accuracyPercentage = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
  const reason = gameType === 'multiplayer' 
    ? `Multiplayer quiz completed - Rank ${gameContext?.rank || 'N/A'}/${gameContext?.totalPlayers || 'N/A'} with ${accuracyPercentage}% accuracy`
    : `Quiz completed with ${accuracyPercentage}% accuracy`;

  // Award XP
  await supabase.rpc('award_user_xp', {
    p_user_id: userId,
    p_amount: xpAmount,
    p_source: gameType === 'multiplayer' ? 'multiplayer_completion' : 'quiz_completion',
    p_description: reason
  });

  // Step 3: Recompute achievements from play history (server-side, idempotent).
  // This also refreshes user_streaks, which the results screen reads below.
  console.log('🏆 [batchProcessGamification] Step 3: Evaluating achievements...');
  const evaluated = await evaluateAchievements(userId);
  const newAchievements: UserAchievement[] = newlyUnlockedAchievements(evaluated);

  // Step 5: Fetch all "after" data in parallel
  console.log('📈 [batchProcessGamification] Step 4: Fetching updated user data...');
  const [afterXPResult, afterAchievementsResult, afterStreakResult] = await Promise.allSettled([
    supabase
      .from('user_xp')
      .select('*')
      .eq('user_id', userId)
      .single(),
    supabase
      .from('user_achievements')
      .select(`
        *,
        achievement:achievements(*)
      `)
      .eq('user_id', userId),
    supabase
      .from('user_streaks')
      .select('*')
      .eq('user_id', userId)
      .single()
  ]);

  const afterXP = afterXPResult.status === 'fulfilled' ? afterXPResult.value.data : null;
  const afterAchievements = afterAchievementsResult.status === 'fulfilled' ? afterAchievementsResult.value.data || [] : [];
  const streakInfo = afterStreakResult.status === 'fulfilled' ? afterStreakResult.value.data : null;

  // Step 6: Calculate results
  console.log('🧮 [batchProcessGamification] Step 5: Calculating results...');
  const earnedXP = (afterXP?.total_xp || 0) - (beforeXP?.total_xp || 0);
  const beforeLevel = beforeXP?.current_level || 1;
  const afterLevel = afterXP?.current_level || 1;
  const levelUp = afterLevel > beforeLevel;

  console.log('✅ [batchProcessGamification] Batch processing completed:', {
    earnedXP,
    levelUp,
    newLevel: afterLevel,
    newAchievements: newAchievements.length,
    streakDays: streakInfo?.current_streak || 0
  });

  return {
    beforeXP,
    afterXP,
    beforeAchievements,
    afterAchievements,
    streakInfo,
    earnedXP,
    levelUp,
    newLevel: afterLevel,
    newAchievements
  };
}

/**
 * Calculate XP amount with enhanced partial credit support
 */
function calculateEnhancedXPAmount(params: BatchGamificationParams): number {
  const { score, totalQuestions, gameType, gameContext, hasPartialCredit, bonusPoints, finalScore } = params;
  
  // Use enhanced final score if available, otherwise fall back to basic score
  const effectiveScore = finalScore || score;
  const scorePercentage = Math.round((effectiveScore / totalQuestions) * 100);
  
  let xpAmount = 0;
  
  if (gameType === 'multiplayer') {
    xpAmount = 15; // Base XP for participation
    
    // Performance bonuses
    const rank = gameContext?.rank || 1;
    const totalPlayers = gameContext?.totalPlayers || 2;
    
    if (rank === 1) {
      xpAmount += 20; // Winner bonus
    } else if (rank <= Math.ceil(totalPlayers / 2)) {
      xpAmount += 10; // Top 50% bonus
    }
    
    // Accuracy bonuses (now supports partial scores)
    if (scorePercentage >= 80) xpAmount += 10;
    if (scorePercentage >= 90) xpAmount += 5;
    if (scorePercentage >= 95) xpAmount += 5; // Near-perfect bonus
    if (scorePercentage === 100) xpAmount += 10;
    
    // Team/group bonuses
    if (gameContext?.familyId) {
      xpAmount += 3; // Team collaboration bonus
    }
    
    // Multiplayer participation bonus
    xpAmount += 5;
  } else {
    // Single player XP calculation with partial credit support
    xpAmount = 10; // Base XP
    if (scorePercentage >= 80) xpAmount += 10; // Good performance
    if (scorePercentage >= 90) xpAmount += 15; // High score bonus
    if (scorePercentage === 100) xpAmount += 10; // Perfect score bonus
  }
  
  // Partial credit bonuses
  if (hasPartialCredit) {
    xpAmount += 3; // Bonus for attempting complex questions with partial credit
  }
  
  // Bonus points from time/performance
  if (bonusPoints && bonusPoints > 0) {
    xpAmount += Math.round(bonusPoints * 2); // Convert bonus points to XP
  }
  
  return xpAmount;
}

/**
 * Legacy XP calculation for backward compatibility
 */
function _calculateXPAmount(score: number, totalQuestions: number, gameType: 'single' | 'multiplayer', gameContext?: any): number {
  return calculateEnhancedXPAmount({
    userId: '',
    score,
    totalQuestions,
    correctCount: Math.max(0, Math.min(totalQuestions, Math.round(score))),
    categoryId: 1,
    difficulty: 'medium',
    gameType,
    gameContext
  });
}

// Export is already done in the function declaration above
